import React, { ReactNode, useCallback, useContext, useMemo } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { Steps } from "@shared/features/bots/BotForm/common/types.ts";
import PageTabs, { TabItem } from "common/Tabs/PageTabs.tsx";
import styled from "styled-components";
import { SummaryInnerWrapper } from "features/bots/BotForm/common/styles.ts";
import { BOTTOM_SUMMARY_CLASS } from "common/constants.ts";

interface MobileBotFormProps {
  valuesModifiedMessage: ReactNode;
  copiedToWorkspaceMessage: ReactNode;
  tabsItems: {
    key: Steps;
    label: string | React.JSX.Element;
  }[];
  tabToComponent: Record<Steps, React.JSX.Element>;
  summary: ReactNode;
}

const MobileBotForm = ({
  valuesModifiedMessage,
  copiedToWorkspaceMessage,
  tabsItems: propsTabsItems,
  tabToComponent,
  summary,
}: MobileBotFormProps) => {
  const { setTab, isAdvancedView, tab } = useContext(BotFormContext)!;

  const pagePadding = 12;

  const tabsItems = useMemo(() => {
    return propsTabsItems.map((tabItem) => ({
      ...tabItem,
      children: (
        <TabWrapper $padding={pagePadding}>
          {copiedToWorkspaceMessage}
          {valuesModifiedMessage}
          {tabToComponent[tabItem.key]}
        </TabWrapper>
      ),
      forceRender: true,
    }));
  }, [
    propsTabsItems,
    tabToComponent,
    pagePadding,
    valuesModifiedMessage,
    copiedToWorkspaceMessage,
  ]);

  const onPageTabsChange = useCallback((tabItem: TabItem) => {
    setTab(tabItem.key as Steps);
  }, []);

  const pageTabs = (
    <PageTabs
      items={tabsItems}
      onTabChange={onPageTabsChange}
      hideTabs={!isAdvancedView}
      defaultKey={tab}
    />
  );

  return (
    <Wrapper>
      <ContentWrapper>{pageTabs}</ContentWrapper>
      <SummaryInnerWrapper
        $isMobile={true}
        $pagePadding={pagePadding}
        $isBottomNavigator={true}
        className={BOTTOM_SUMMARY_CLASS}
      >
        {summary}
      </SummaryInnerWrapper>
    </Wrapper>
  );
};

export default MobileBotForm;

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const ContentWrapper = styled.div`
  flex: 1;
  min-height: 0;
`;
const TabWrapper = styled.div<{ $padding?: number }>`
  overflow-y: auto;
  padding: ${(p) => p.$padding}px;
  height: 100%;
  scrollbar-gutter: stable;
`;
